/**
 * IMPORTS
 */

import type { IMetadadoCampoVariavel } from "@pages/client/details-model/view-model/helpers/normalizar-linhas-planilhas";

/**
 * Organiza uma linha de planilha garantindo a ordem correta das colunas.
 *
 * Regras:
 * 1. O campo identificador vem primeiro
 * 2. O campo "nome" vem logo depois (regra do backend)
 * 3. Os demais campos seguem a ordem dos metadados
 * 4. Campos extras são adicionados ao final
 */
export function ordenarLinhaPlanilhaPorPrioridade(
  dadosLinha: Record<string, unknown>,
  campoIdentificador: string,
  metadadosCampos: IMetadadoCampoVariavel[]
): Record<string, unknown> {
  const linhaOrdenada: Record<string, unknown> = {};
  const camposJaAdicionados = new Set<string>();

  const possuiCampo = (campo: string) => Object.hasOwn(dadosLinha, campo);

  /**
   * 1. Campo identificador (prioridade máxima)
   */
  if (possuiCampo(campoIdentificador)) {
    linhaOrdenada[campoIdentificador] = dadosLinha[campoIdentificador];
    camposJaAdicionados.add(campoIdentificador);
  }

  /**
   * 2. Campo "nome" (regra do backend)
   */
  const campoNomePrincipal = metadadosCampos.find(
    (campo) => campo.nome.trim().toLowerCase() === "nome"
  )?.nome;

  if (
    campoNomePrincipal &&
    possuiCampo(campoNomePrincipal) &&
    !camposJaAdicionados.has(campoNomePrincipal)
  ) {
    linhaOrdenada[campoNomePrincipal] = dadosLinha[campoNomePrincipal];
    camposJaAdicionados.add(campoNomePrincipal);
  }

  /**
   * 3. Demais campos seguindo metadados
   */
  for (const { nome: nomeCampo } of metadadosCampos) {
    if (camposJaAdicionados.has(nomeCampo)) continue;
    if (!possuiCampo(nomeCampo)) continue;

    linhaOrdenada[nomeCampo] = dadosLinha[nomeCampo];
    camposJaAdicionados.add(nomeCampo);
  }

  /**
   * 4. Campos restantes (não previstos nos metadados)
   */
  for (const nomeCampo of Object.keys(dadosLinha)) {
    if (camposJaAdicionados.has(nomeCampo)) continue;

    linhaOrdenada[nomeCampo] = dadosLinha[nomeCampo];
  }

  return linhaOrdenada;
}
